import { type ReactNode, useId, useRef } from "react";
import { ActionIcon, Box, Popover } from "@mantine/core";
import { useClickOutside, useDisclosure } from "@mantine/hooks";
import { IconInfoCircle } from "@tabler/icons-react";

interface HintProps {
  /** What the icon is, for anyone who cannot see it. */
  "aria-label": string;
  children: ReactNode;
}

/**
 * An icon that explains itself on hover, and the panel it explains itself in.
 *
 * Hover or focus shows the panel as a tooltip -- `role="tooltip"`, `aria-describedby`, and no
 * pointer events, because a tooltip you can chase flickers on the gap between it and its
 * trigger.
 *
 * Clicking pins it instead: the panel stays after the pointer leaves and takes the pointer, so
 * its text can be read at leisure and selected. Pinned it is a `dialog` rather than a tooltip,
 * because a tooltip is a description of its trigger and this has outlived the pointer that
 * asked for it. Escape or a press outside unpins it, as does pressing the trigger again, and
 * a press elsewhere is handled on pointer-down so this closes before that control reacts.
 *
 * Placement is floating-ui's, through Mantine's `Popover`. What used to be here was a
 * hand-written measurement of the room above and below the trigger, written onto the panel
 * with `classList` -- which a re-render then silently undid. `withRoles={false}` because the
 * two roles this panel takes are the component's own business and Mantine's default pair
 * (`dialog` plus `aria-expanded` on every open) is only right for half of them.
 */
export function Hint({ "aria-label": ariaLabel, children }: HintProps) {
  const [hovered, { open: enter, close: leave }] = useDisclosure(false);
  const [pinned, { close: unpinOnly, toggle: togglePin }] = useDisclosure(false);
  // `pointerdown` rather than the hook's default pair, because of the promise above: this
  // closes before the control underneath it reacts. The panel is portalled, so it lies
  // outside this ref -- which is why a press inside a pinned panel unpins it, exactly as it
  // did when the listener was hand-written. That is the intended behaviour, not a leak.
  const rootRef = useClickOutside<HTMLDivElement>(unpinOnly, ["pointerdown"], undefined, pinned);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const id = useId();
  const open = hovered || pinned;

  // Focus first, then clear: returning focus to the trigger fires its own `focus` handler,
  // and letting that land last would reopen the panel as a tooltip the instant it closed.
  const unpin = () => {
    triggerRef.current?.focus();
    unpinOnly();
    leave();
  };

  return (
    <Box
      display="flex"
      ref={rootRef}
      onPointerEnter={enter}
      onPointerLeave={leave}
      onKeyDown={(event) => {
        if (event.key !== "Escape" || !open) return;
        // Stopped, or a hint opened inside a dialog closes the dialog with it.
        event.stopPropagation();
        if (pinned) unpin();
        else leave();
      }}
    >
      <Popover
        opened={open}
        position="top-end"
        withinPortal
        withRoles={false}
        middlewares={{ flip: true, shift: true }}
        trapFocus={false}
        returnFocus={false}
        styles={{ dropdown: { pointerEvents: pinned ? "auto" : "none" } }}
      >
        <Popover.Target>
          <ActionIcon
            ref={triggerRef}
            size="sm"
            // A test hook, not a style: `Hint.test.tsx` drives the trigger through it.
            className="hint__mark"
            aria-label={ariaLabel}
            aria-expanded={pinned}
            // Only while it is a tooltip: pinned, the panel is a dialog and describes nothing.
            aria-describedby={open && !pinned ? id : undefined}
            onFocus={enter}
            onBlur={leave}
            onClick={togglePin}
          >
            {/* Owned here rather than passed in: both hints are the same question -- what does
                this marking mean. */}
            <IconInfoCircle size={16} stroke={1.5} aria-hidden="true" focusable="false" />
          </ActionIcon>
        </Popover.Target>

        <Popover.Dropdown
          id={id}
          role={pinned ? "dialog" : "tooltip"}
          aria-label={pinned ? ariaLabel : undefined}
        >
          {children}
        </Popover.Dropdown>
      </Popover>
    </Box>
  );
}
